/** Публичные ручки meetrooms API v2: карточка события и запись на него. */

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api.meetrooms.ru/api/v2'

export interface EventCard {
  uuid: string
  title: string | null
  description: string | null
  startTime: string
  endTime: string
  capacity: number | null
  takenSeats: number
  seatsLeft: number | null
  room: {
    id: number
    name: string
    capacity: number
    location: {
      id: number
      name: string
      address: string | null
      timezone: string
    }
  }
}

export interface LocationOption {
  id: number
  name: string
}

export interface SignupPayload {
  firstName: string
  lastName: string
  phone: string
  email?: string
  comment?: string
  locationId: number
}

/** Ошибка с текстом от бэка — его показываем человеку как есть. */
export class ApiError extends Error {}

/**
 * Запрос к API. `apiKey` — X-Api-Key (страница резидента); `unwrap: false` —
 * ответ без конверта `{data}` (расписание).
 */
export async function request<T>(
  path: string,
  init?: RequestInit,
  { apiKey, unwrap = true }: { apiKey?: string; unwrap?: boolean } = {},
): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(apiKey ? { 'X-Api-Key': apiKey } : {}),
      ...init?.headers,
    },
  })
  const body = await response.json().catch(() => null)
  if (!response.ok) {
    // API отдаёт {error, code, message}; `detail` — от FastAPI до нашего обработчика.
    throw new ApiError(body?.message ?? body?.detail ?? 'Что-то пошло не так')
  }
  if (!unwrap) return body as T
  if (!body || !('data' in body)) {
    throw new ApiError('Сервер ответил не тем, чем ожидали')
  }
  return body.data as T
}

export const getEvent = (uuid: string) => request<EventCard>(`/events/${uuid}`)

export interface BookingPage {
  name: string
  roomIds: number[]
  resourceIds: number[]
}

/** Сборная страница /a/<code>: что показывать, настроено в админке. */
export const getBookingPage = (code: string) =>
  request<BookingPage>(`/booking-pages/by-code/${encodeURIComponent(code)}`)

/** Мероприятия комнат за день (YYYY-MM-DD) — для сборной страницы. */
export const getEvents = (roomIds: number[], date: string) =>
  request<EventCard[]>(
    `/events?${roomIds.map((id) => `roomIds=${id}`).join('&')}&date=${date}`,
  )

export const getLocations = () =>
  request<LocationOption[]>('/locations?perPage=100&sort=name')

export const signUp = (uuid: string, payload: SignupPayload) =>
  request<{ id: number }>(`/events/${uuid}/registrations`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
